import { BuildingLibraryIcon, CogIcon, KeyIcon, PencilSquareIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { GitBranchIcon } from "@primer/octicons-react";
import classNames from "classnames";
import Link from "next/link";
import { ReactNode, useMemo } from "react";
import { WEB_URL } from "../../../.env.current";
import { getCurrentUrl } from "../../util/router";
import LayoutApp from "./App";

const NAVIGATION_SETTINGS = [
  {
    name: "Edit profile",
    icon: UserCircleIcon,
    url: new URL(`${WEB_URL}/settings`),
  },
  {
    name: "Account",
    icon: BuildingLibraryIcon,
    url: new URL(`${WEB_URL}/settings/account`),
  },
  {
    name: "Editor",
    icon: PencilSquareIcon,
    url: new URL(`${WEB_URL}/settings/editor`),
  },
  {
    name: "Git",
    icon: GitBranchIcon,
    url: new URL(`${WEB_URL}/settings/git`),
  },
  {
    name: "Tokens",
    icon: KeyIcon,
    url: new URL(`${WEB_URL}/settings/tokens`),
  },
];

export default function LayoutSettings({ children }: { children: ReactNode }) {
  const navActive = useMemo(() => {
    const u = getCurrentUrl();
    return NAVIGATION_SETTINGS.find((i) => i.url.pathname === u?.pathname);
  }, []);

  return (
    <LayoutApp
      documentTitle={navActive && `${navActive.name} ⚡︎ Settings`}
      title={
        <div className="w-full flex items-center">
          <Link href="/settings" className="flex items-center">
            <CogIcon className="inline h-5 w-auto mr-2" aria-hidden="true" />
            Settings
          </Link>
        </div>
      }
      yfsClient={undefined}
      awareness={undefined}
    >
      <div className="h-full mx-auto max-w-(--breakpoint-xl)">
        <div className="rounded-lg bg-gray-100/40 border border-gray-400">
          <div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
            <aside className="py-6 lg:col-span-3">
              <nav className="space-y-1">
                {NAVIGATION_SETTINGS.map((item) => {
                  const isCurrent = navActive?.name === item.name;
                  return (
                    <Link
                      key={item.name}
                      href={item.url.href}
                      className={classNames(
                        isCurrent
                          ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                          : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                        "group border-l-4 px-3 py-2 flex items-center text-sm font-medium"
                      )}
                      aria-current={isCurrent ? "page" : undefined}
                    >
                      <item.icon
                        className={classNames(
                          isCurrent
                            ? "text-teal-500 group-hover:text-teal-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "shrink-0 -ml-1 mr-3 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>

            <div className="divide-y divide-gray-200 lg:col-span-9">
              <div className="py-6 px-4 sm:px-6 lg:pb-8 min-h-[35vh]">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </LayoutApp>
  );
}
